import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";
import { JOURS, formatHeure } from "utils/planning";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { formatDate } from "utils/formatDate";
import { libelleMatiere as libelleMatiereCode } from "utils/valeurs";
import PersonIcon from "@mui/icons-material/Person";
import SubjectIcon from "@mui/icons-material/Subject";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import GroupIcon from "@mui/icons-material/Group";

const MATIERES = {
  quoran: "Qour'an",
  arabe: "Langue arabe",
  science: "Sciences de la religion",
};

const TYPES = {
  individuel: { label: "valeurs.typeCours.individuel", bg: "#1A73E8" },
  groupe: { label: "valeurs.typeCours.groupe", bg: "#7b1fa2" },
  general: { label: "calendrier.general", bg: "#fb8c00" },
};

const getCouleur = (type) => (TYPES[type] || TYPES.individuel).bg;

// ── Helpers semaine (dates locales) ──
const lundiDe = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const ajouterJours = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const formatCourt = (d) =>
  formatDate(d, undefined, "jourCourt").replace(/\./g, "");

const libelleMatiere = (o) =>
  o.type === "general" ? o.commentaire_general || i18n.t("valeurs.typeCours.general") : libelleMatiereCode(o.matiere) || "—";
// Tous les co-enseignants du cours (principal en premier)
const libelleEnseignants = (o) => (o.co_enseignants?.length ? o.co_enseignants.join(", ") : o.enseignant) || "—";
const libellePublic = (o) =>
  o.type === "general" ? i18n.t("calendrier.publicLibre") : o.eleve || o.groupe || "—";

function LigneInfo({ icon: IconInfo, children }) {
  return (
    <MDBox display="flex" alignItems="flex-start" gap={0.75} mt={0.5}>
      <IconInfo sx={{ fontSize: "16px !important", color: "text.secondary", mt: "1px", flexShrink: 0 }} />
      <MDBox component="span" sx={{ fontSize: 13, color: "text.primary" }}>{children}</MDBox>
    </MDBox>
  );
}

function TooltipContenu({ o, titre = true }) {
  const { t } = useTranslation();
  return (
    <MDBox sx={{ lineHeight: 1.5 }}>
      {titre && <MDBox sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mb: 0.5 }}>{libelleMatiere(o)}</MDBox>}
      <LigneInfo icon={PersonIcon}>{libelleEnseignants(o)}</LigneInfo>
      <LigneInfo icon={o.type === "individuel" ? SubjectIcon : GroupIcon}>
        {t(o.type === "groupe" ? "champs.groupe" : o.type === "general" ? "calendrier.public" : "champs.eleve")} : {libellePublic(o)}
      </LigneInfo>
      <LigneInfo icon={AccessTimeIcon}>{o.heure_cours ? formatHeure(o.heure_cours) : "—"}</LigneInfo>
      {o.lieu && <LigneInfo icon={PlaceIcon}>{o.lieu}</LigneInfo>}
    </MDBox>
  );
}

function CoursChip({ o, onClick, onMouseEnter, onMouseLeave, full = false }) {
  const t = TYPES[o.type] || TYPES.individuel;
  const heure = o.heure_cours ? formatHeure(o.heure_cours) : "—";
  return (
    <Chip
      size="small"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      label={full ? i18n.t(t.label) : `${heure} · ${o.type === "general" || !o.matiere ? libelleMatiere(o) : libelleMatiereCode(o.matiere, true)}`}
      sx={{
        background: t.bg, color: "#fff", fontWeight: 600, fontSize: 11,
        maxWidth: full ? "none" : "100%", cursor: "pointer",
        "&:hover": { background: t.bg, filter: "brightness(0.9)" },
        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
      }}
    />
  );
}

function SelecteurSemaine({ lundi, setLundi }) {
  const { t } = useTranslation();
  const dimanche = ajouterJours(lundi, 6);
  return (
    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
      <MDButton variant="outlined" color="dark" size="small" onClick={() => setLundi(ajouterJours(lundi, -7))}>
        <Icon>chevron_left</Icon>&nbsp;{t("calendrier.semainePrecedente")}
      </MDButton>
      <MDTypography variant="button" fontWeight="bold" sx={{ mx: 1, minWidth: 190, textAlign: "center" }}>
        {formatCourt(lundi)} → {formatCourt(dimanche)}
      </MDTypography>
      <MDButton variant="outlined" color="dark" size="small" onClick={() => setLundi(ajouterJours(lundi, 7))}>
        {t("calendrier.semaineSuivante")}&nbsp;<Icon>chevron_right</Icon>
      </MDButton>
      <MDButton variant="text" color="info" size="small" onClick={() => setLundi(lundiDe(new Date()))}>
        {t("commun.aujourdhui")}
      </MDButton>
    </MDBox>
  );
}

function Legende() {
  return (
    <MDBox display="flex" gap={2} flexWrap="wrap" mb={2}>
      {Object.values(TYPES).map((t) => (
        <MDBox key={t.label} display="flex" alignItems="center" gap={0.5}>
          <MDBox sx={{ width: 12, height: 12, borderRadius: "50%", background: t.bg }} />
          <MDTypography variant="caption">{i18n.t(t.label)}</MDTypography>
        </MDBox>
      ))}
    </MDBox>
  );
}

// ── Vue globale : lignes = enseignants, colonnes = jours ──
function VueGlobale({ lundi, setLundi }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const sansEns = t("calendrier.sansEnseignant");
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCours, setSelectedCours] = useState(null);
  // Mobile (xs/sm) : détail dans un Drawer bas ouvert au tap ; desktop : panneau latéral au survol
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setLoading(true);
    api.get(`/cours/planning?semaine=${toIso(lundi)}`)
      .then(({ data }) => setOccurrences(data))
      .catch((e) => console.error("Erreur planning:", e))
      .finally(() => setLoading(false));
  }, [lundi]);

  const enseignants = useMemo(
    () => [...new Set(occurrences.map((o) => o.enseignant || sansEns))]
      .sort((a, b) => a.localeCompare(b, "fr")),
    [occurrences, sansEns]
  );

  const cellule = (ens, jour) => occurrences.filter(
    (o) => (o.enseignant || sansEns) === ens && o.jour === jour
  );

  const aujourdhui = toIso(new Date());

  return (
    <MDBox>
      <SelecteurSemaine lundi={lundi} setLundi={setLundi} />
      <Legende />
      {loading ? (
        <MDBox textAlign="center" py={6}><CircularProgress size={32} /></MDBox>
      ) : enseignants.length === 0 ? (
        <MDTypography variant="button" color="text">{t("calendrier.aucunCoursSemaine")}</MDTypography>
      ) : (
        <MDBox sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "flex-start" }}>
        <MDBox sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "auto" }, overflowX: "auto" }}>
          <MDBox sx={{
            display: "grid",
            gridTemplateColumns: `180px repeat(${JOURS.length}, minmax(130px, 1fr))`,
            gap: "4px", minWidth: 180 + JOURS.length * 134,
          }}>
            {/* En-tête */}
            <MDBox />
            {JOURS.map((j, i) => {
              const date = ajouterJours(lundi, i);
              const estAujourdhui = toIso(date) === aujourdhui;
              return (
                <MDBox key={j.value} sx={{
                  textAlign: "center", py: 1, borderRadius: "8px",
                  background: estAujourdhui ? "#2e7d32" : "#f0f2f5",
                  color: estAujourdhui ? "#fff" : "#344767",
                }}>
                  <MDTypography variant="caption" fontWeight="bold" sx={{ color: "inherit" }}>
                    {j.label} {date.getDate()}/{date.getMonth() + 1}
                  </MDTypography>
                </MDBox>
              );
            })}

            {/* Lignes enseignants */}
            {enseignants.map((ens) => (
              <MDBox key={ens} sx={{ display: "contents" }}>
                <MDBox sx={{
                  display: "flex", alignItems: "center", px: 1, borderRadius: "8px",
                  background: "#f8f9fa", minHeight: 44,
                }}>
                  <Tooltip title={ens} disableHoverListener={ens.length <= 18}>
                    <MDTypography variant="button" fontWeight="bold" noWrap>
                      {ens.length > 18 ? `${ens.slice(0, 18)}…` : ens}
                    </MDTypography>
                  </Tooltip>
                </MDBox>
                {JOURS.map((j) => {
                  const items = cellule(ens, j.value);
                  return (
                    <MDBox key={j.value} sx={{
                      display: "flex", flexDirection: "column", gap: "3px", p: "4px",
                      borderRadius: "8px", minHeight: 44,
                      background: items.length ? "#fff" : "#f5f5f5",
                      border: items.length ? "1px solid #e9ecef" : "none",
                    }}>
                      {items.map((o) => (
                        <CoursChip key={`${o.id}-${o.jour}`} o={o}
                          onClick={(e) => {
                            if (isMobile) { e.stopPropagation(); setSelectedCours(o); } else navigate(`/cours/${o.id}`);
                          }}
                          onMouseEnter={isMobile ? undefined : () => setSelectedCours(o)} />
                      ))}
                    </MDBox>
                  );
                })}
              </MDBox>
            ))}
          </MDBox>
        </MDBox>

        {/* Panneau détail : dernier cours survolé (reste affiché jusqu'au survol suivant) */}
        {!isMobile && (
        <MDBox sx={{
          width: { xs: "100%", md: 260 }, flexShrink: 0,
          position: { xs: "static", md: "sticky" }, top: 80,
        }}>
          {selectedCours ? (
            <Paper sx={{
              p: 2, borderRadius: 2, boxShadow: 3,
              borderLeft: `4px solid ${getCouleur(selectedCours.type)}`,
              transition: "all 0.15s ease",
            }}>
              <TooltipContenu o={selectedCours} />
              <MDButton variant="text" color="info" size="small" sx={{ mt: 1.5, p: 0, minHeight: 0 }}
                onClick={() => navigate(`/cours/${selectedCours.id}`)}>
                {t("calendrier.voirCours")}
              </MDButton>
            </Paper>
          ) : (
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <MDTypography variant="body2" color="text">
                {t("calendrier.survolez")}
              </MDTypography>
            </Paper>
          )}
        </MDBox>
        )}
        </MDBox>
      )}

      {isMobile && (
        <Drawer
          anchor="bottom"
          open={Boolean(selectedCours)}
          onClose={() => setSelectedCours(null)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              px: 2,
              pt: 1,
              pb: 3,
              maxHeight: "60vh",
            },
          }}
        >
          {/* Barre de poignée */}
          <MDBox sx={{ width: 40, height: 4, bgcolor: "grey.300", borderRadius: 2, mx: "auto", mb: 2 }} />
          {selectedCours && (
            <>
              <MDBox sx={{ borderLeft: `4px solid ${getCouleur(selectedCours.type)}`, pl: 1.5, mb: 1.5 }}>
                <MDTypography variant="h6" fontWeight="bold">{libelleMatiere(selectedCours)}</MDTypography>
              </MDBox>
              <TooltipContenu o={selectedCours} titre={false} />
              <MDButton variant="outlined" color="info" fullWidth sx={{ mt: 1.5 }}
                onClick={() => { navigate(`/cours/${selectedCours.id}`); setSelectedCours(null); }}>
                {t("calendrier.voirCours")}
              </MDButton>
            </>
          )}
        </Drawer>
      )}
    </MDBox>
  );
}

// ── Vue filtrée : par enseignant / matière / groupe ──
function VueFiltree({ lundi, setLundi }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState("enseignant");
  const [enseignants, setEnseignants] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [valeur, setValeur] = useState(null);
  const [filtreActif, setFiltreActif] = useState(null); // { mode, valeur } validé par "Afficher"
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cours/enseignants").then(({ data }) => setEnseignants(data)).catch(() => {});
    api.get("/groupes").then(({ data }) => setGroupes(data)).catch(() => {});
  }, []);

  const options = {
    enseignant: enseignants.map((e) => ({ value: e, label: e })),
    matiere: Object.keys(MATIERES).map((value) => ({ value, label: libelleMatiereCode(value) })),
    groupe: groupes.map((g) => ({ value: String(g.id), label: g.nom })),
  }[mode];

  useEffect(() => {
    if (!filtreActif) return;
    const param = { enseignant: "enseignant", matiere: "matiere", groupe: "groupe_id" }[filtreActif.mode];
    setLoading(true);
    api.get(`/cours/planning?semaine=${toIso(lundi)}&${param}=${encodeURIComponent(filtreActif.valeur.value)}`)
      // L'API renvoie une occurrence par co-enseignant : un seul créneau par cours/jour dans la liste
      .then(({ data }) => setOccurrences(data.filter((o, i, arr) =>
        arr.findIndex((x) => x.id === o.id && x.jour === o.jour) === i)))
      .catch((e) => console.error("Erreur planning:", e))
      .finally(() => setLoading(false));
  }, [filtreActif, lundi]);

  const libelleJour = (o) => {
    const j = JOURS.find((x) => x.value === o.jour);
    const [, m, d] = o.date_iso.split("-");
    return `${j ? j.label : o.jour} ${Number(d)}/${Number(m)}`;
  };

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <RadioGroup row value={mode} onChange={(e) => { setMode(e.target.value); setValeur(null); }}>
          <FormControlLabel value="enseignant" control={<Radio />} label={t("calendrier.parEnseignant")} />
          <FormControlLabel value="matiere" control={<Radio />} label={t("calendrier.parMatiere")} />
          <FormControlLabel value="groupe" control={<Radio />} label={t("calendrier.parGroupe")} />
        </RadioGroup>
        <Autocomplete
          sx={{ minWidth: { xs: "100%", sm: 260 } }}
          size="small"
          options={options}
          value={valeur}
          onChange={(_, v) => setValeur(v)}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          getOptionLabel={(o) => o.label}
          renderInput={(params) => (
            <TextField {...params} label={t({ enseignant: "champs.enseignant", matiere: "champs.matiere", groupe: "champs.groupe" }[mode])} />
          )}
        />
        <MDButton variant="gradient" color="success" size="small" disabled={!valeur}
          onClick={() => setFiltreActif({ mode, valeur })}>
          <Icon>search</Icon>&nbsp;{t("commun.afficher")}
        </MDButton>
      </MDBox>

      <SelecteurSemaine lundi={lundi} setLundi={setLundi} />

      {!filtreActif ? (
        <MDTypography variant="button" color="text">
          {t("calendrier.choisirFiltre")}
        </MDTypography>
      ) : loading ? (
        <MDBox textAlign="center" py={6}><CircularProgress size={32} /></MDBox>
      ) : occurrences.length === 0 ? (
        <MDTypography variant="button" color="text">
          {t("calendrier.aucunCoursPour", { nom: filtreActif.valeur.label })}
        </MDTypography>
      ) : (
        <MDBox>
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            {t("calendrier.creneaux", { count: occurrences.length })} — {filtreActif.valeur.label}
          </MDTypography>
          {occurrences.map((o) => (
            <MDBox key={`${o.id}-${o.jour}`} onClick={() => navigate(`/cours/${o.id}`)} sx={{
              display: "grid", alignItems: "center", gap: 1.5, cursor: "pointer",
              gridTemplateColumns: { xs: "1fr", md: "110px 60px 100px 1fr 1fr 1fr" },
              px: 2, py: 1.2, borderBottom: "1px solid #f0f2f5",
              "&:hover": { background: "#f8fff8" },
            }}>
              <MDTypography variant="button" fontWeight="bold">{libelleJour(o)}</MDTypography>
              <MDTypography variant="button">{o.heure_cours ? formatHeure(o.heure_cours) : "—"}</MDTypography>
              <MDBox><CoursChip o={o} full /></MDBox>
              <MDTypography variant="caption">{libelleMatiere(o)}</MDTypography>
              <MDTypography variant="caption" color="text">{libellePublic(o)}</MDTypography>
              <MDTypography variant="caption" color="text">{libelleEnseignants(o)}</MDTypography>
            </MDBox>
          ))}
        </MDBox>
      )}
    </MDBox>
  );
}

function Calendrier() {
  const { t } = useTranslation();
  const [onglet, setOnglet] = useState(0);
  const [lundi, setLundi] = useState(() => lundiDe(new Date()));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(46,125,50,0.4)",
              }}>
                <MDTypography variant="h5" color="white" fontWeight="bold">{t("menu.calendrier")}</MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                  {t("calendrier.sousTitre")}
                </MDTypography>
              </MDBox>
              <MDBox pt={2} px={3} pb={3}>
                <Tabs value={onglet} onChange={(_, v) => setOnglet(v)} sx={{ mb: 2, maxWidth: 400 }}>
                  <Tab label={t("calendrier.vueGlobale")} />
                  <Tab label={t("calendrier.vueFiltree")} />
                </Tabs>
                {onglet === 0
                  ? <VueGlobale lundi={lundi} setLundi={setLundi} />
                  : <VueFiltree lundi={lundi} setLundi={setLundi} />}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Calendrier;
